/**
 * AboutPage — Public about / methodology / curator page.
 * Route: /about
 */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Rss, Headphones, FileText, Newspaper, Mail, Award, Globe2, Cpu } from "lucide-react";
import PublicPageNav from "../components/PublicPageNav";
import PublicPageFooter from "../components/PublicPageFooter";

export default function AboutPage() {
  useEffect(() => {
    document.title = "About TourismIntel — Curator Mirko Lalli & Editorial Methodology";
    const desc = document.querySelector('meta[name="description"]');
    const original = desc?.getAttribute("content");
    desc?.setAttribute(
      "content",
      "TourismIntel is curated by Mirko Lalli, EU-recognised expert on AI and tourism. Editorial methodology, source selection criteria, AI summarisation pipeline, and how DMOs and hoteliers can subscribe."
    );
    return () => { if (original) desc?.setAttribute("content", original); };
  }, []);

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh", fontFamily: "'Space Grotesk', sans-serif" }}>
      <PublicPageNav active="about" />

      {/* Hero */}
      <section style={{ background: "#fff", borderBottom: "1px solid #e8ecee", padding: "56px 24px 40px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6b7a7d" }}>About</span>
          </div>
          <h1
            data-testid="about-page-title"
            style={{ margin: 0, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 700, letterSpacing: "-0.03em", color: "#191c1d", lineHeight: 1.1, fontFamily: "'Reckless Neue Variable', serif" }}
          >
            Daily travel intelligence, edited by humans, scaled by AI.
          </h1>
          <p style={{ margin: "16px 0 0", fontSize: 17, color: "#3b494c", lineHeight: 1.6 }}>
            All the stories that matter in AI and travel, explained. A weekly brief, newsletter and podcast, plus Pulse: Italy&apos;s tourism in official numbers and forecasts.
            We read the travel industry every twelve hours and publish one
            curated brief: tourism intelligence, not tourism noise.
          </p>
        </div>
      </section>

      {/* Curator */}
      <section style={{ padding: "48px 24px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <Award style={{ width: 18, height: 18, color: "#006874" }} />
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "#191c1d", fontFamily: "'Reckless Neue Variable', serif" }}>
              Curated by Mirko Lalli
            </h2>
          </div>
          <p style={{ fontSize: 15, color: "#3b494c", lineHeight: 1.7 }}>
            Mirko Lalli is an EU-recognised expert on AI and tourism. He has
            advised regional tourism boards, hotel groups and travel-tech
            companies across Europe on data, AI strategy and digital
            transformation. Phocuswright cites his work as one of the
            reference voices in the Italian and European DMO conversation.
          </p>
          <p style={{ marginTop: 12 }}>
            <a
              href="https://mirkolalli.me"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="curator-link"
              style={{ color: "#006874", fontWeight: 600, textDecoration: "underline" }}
            >
              mirkolalli.me →
            </a>
          </p>
        </div>
      </section>

      {/* Methodology */}
      <section style={{ background: "#fff", padding: "48px 24px", borderTop: "1px solid #e8ecee", borderBottom: "1px solid #e8ecee" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <Cpu style={{ width: 18, height: 18, color: "#006874" }} />
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "#191c1d", fontFamily: "'Reckless Neue Variable', serif" }}>
              Editorial methodology
            </h2>
          </div>
          <ol style={{ margin: "12px 0 0", paddingLeft: 20, fontSize: 15, color: "#3b494c", lineHeight: 1.8 }}>
            <li>
              <strong>Verified RSS sources</strong> across editorial publications
              (Skift, Phocuswright, FT Travel, Hospitality Net, Hotel Technology
              News, Lodging Magazine, FlightGlobal, Airline Weekly, Globetrender,
              VentureBeat AI, L&apos;Agenzia di Viaggi, ETC Corporate) plus Reddit
              communities for ground-truth sentiment.
            </li>
            <li>
              <strong>Automated discovery</strong> for stories
              behind paywalls or Cloudflare. Five topic queries refreshed every
              12 hours: AI in travel, hotel tech, DMO strategy, aviation AI,
              travel-tech M&amp;A.
            </li>
            <li>
              <strong>Per-cluster ranking</strong> by number of distinct editorial
              sources covering the same story (cross-source citation count),
              then optional Claude Opus 4.5 editorial curation for the ticker.
            </li>
            <li>
              <strong>Concise AI summaries</strong> capped at 150 words via
              GPT-4o-mini or Claude — every story is read, distilled, and
              tagged with extracted topics.
            </li>
            <li>
              <strong>Human editorial filter</strong> on the weekly Travel
              Intelligence Brief and podcast: Mirko reviews, contextualises,
              and adds B2B perspective before publishing.
            </li>
          </ol>
        </div>
      </section>

      {/* What we publish */}
      <section style={{ padding: "48px 24px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <Globe2 style={{ width: 18, height: 18, color: "#006874" }} />
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "#191c1d", fontFamily: "'Reckless Neue Variable', serif" }}>
              What we publish
            </h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            {[
              { icon: FileText,    label: "Weekly Travel Brief", to: "/travel-brief",  sub: "Editorial deep-dive every Monday" },
              { icon: Headphones,  label: "TourismIntel Weekly Podcast", to: "/podcast", sub: "20-min audio briefing" },
              { icon: Newspaper,   label: "Newsletter Archive",   to: "/newsletters",   sub: "Email digests, fully searchable" },
              { icon: Rss,         label: "6 RSS feeds",          to: "/subscribe",     sub: "Machine-readable for any reader or agent" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`about-link-${item.to.replace("/", "")}`}
                  style={{
                    background: "#fff",
                    border: "1px solid #e8ecee",
                    borderRadius: 14,
                    padding: 16,
                    textDecoration: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <Icon style={{ width: 18, height: 18, color: "#006874" }} />
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#191c1d" }}>{item.label}</p>
                  <p style={{ margin: 0, fontSize: 12, color: "#6b7a7d", lineHeight: 1.5 }}>{item.sub}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section style={{ background: "#fff", borderTop: "1px solid #e8ecee", padding: "40px 24px" }}>
        <div style={{ maxWidth: 820, margin: "0 auto", textAlign: "center" }}>
          <Mail style={{ width: 20, height: 20, color: "#006874", marginBottom: 8 }} />
          <p style={{ margin: 0, fontSize: 15, color: "#3b494c" }}>
            Have a tip, scoop, or partnership idea? Reach us at{" "}
            <a href="mailto:hello@tourismintel.ai" style={{ color: "#006874", fontWeight: 600 }} data-testid="contact-email-link">
              hello@tourismintel.ai
            </a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <PublicPageFooter />
    </div>
  );
}
